import { yupResolver } from "@hookform/resolvers/yup";
import { Checkbox, Col, Form, Input, Row } from "antd";
import dayjs from "dayjs";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import entityType from "../../api/entityType";
import {
  exportReport,
  fetchReport,
  resetReportState,
} from "../../store/report/reportActions";
import { transformTextSummaryInput } from "../../store/retention/retentionActions";
import PermissionWrapper from "../navigation/PermissionWrapper";
import ReportPanel from "../shared/ReportPanel";
import AgencyMultiSelectEx from "../shared/form/AgencyMultiSelectEx";
import DrillDownSortFieldSelectEx from "../shared/form/DrillDownSortFieldSelectEx";
import ExtraReportFilterContainer from "../shared/form/ExtraReportFiltersContainer";
import ProducerSelectEx from "../shared/form/ProducerSelectEx";
import {
  dateRangeConfigs,
  fixedDateRanges,
} from "../shared/form/fixedDateRanges";
import { getInitialAgency } from "../shared/form/getInitialAgency";
import AdvancedRangePicker from "../shared/form/reactHookForm/AdvancedRangePicker";
import { dateFormat, toTZNeutralFormat } from "../shared/formatting";
import { ViewRetentionData } from "../shared/securityRoleTasks";
import { agenciesListEqual } from "../shared/sorting";
import { basicRetentionReportFieldOptions } from "./reportParameters";
import { textSummaryReportSchema } from "./reportSchema";

// Constants
const partialInitialValues = {
  AgencyKeys: [],
  FixedDateRange: fixedDateRanges.LAST_MONTH,
  ExpiryDate: ["", ""],
  IncludeLateRenewals: false,
  Keyword: "1",
  DrillDownSortFields: [],
  ProducerCode1Keys: [],
  ProducerCode2Keys: [],
};

const defaultLayout = {
  labelCol: {
    xs: { span: 5 },
    sm: { span: 5 },
    md: { span: 5 },
    lg: { span: 8 },
    xl: { span: 6 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 19 },
    sm: { span: 19 },
    md: { span: 19 },
    lg: { span: 16 },
    xl: { span: 18 },
    xxl: { span: 19 },
  },
};

const specialLayout = {
  labelCol: {
    xs: { span: 5 },
    sm: { span: 7 },
    md: { span: 6 },
    lg: { span: 9 },
    xl: { span: 7 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 19 },
    sm: { span: 17 },
    md: { span: 18 },
    lg: { span: 15 },
    xl: { span: 17 },
    xxl: { span: 19 },
  },
};

const reportName = "TextSummaryByReplyTypeReport";

const TextSummaryReport = () => {
  // Form
  const {
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    trigger,
    formState: { isValid, isDirty, defaultValues, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: partialInitialValues,
    resolver: yupResolver(textSummaryReportSchema),
  });

  //
  const dispatch = useDispatch();

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [initialDefaultValues, setInitialDefaultValues] =
    useState(partialInitialValues);

  //
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const agencies = useSelector((state) => state.agency.listCompact);

  //
  useEffect(() => {
    if (
      agencies != null &&
      !isEmpty(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      var initialAgency = getInitialAgency(homeAgency, agencies);
      const fullInitialValues = {
        ...partialInitialValues,
        AgencyKeys: [initialAgency],
        ExpiryDate: [
          toTZNeutralFormat(dayjs().subtract(1, "month").startOf("month")),
          toTZNeutralFormat(dayjs().subtract(1, "month").endOf("month")),
        ],
      };

      setInitialDefaultValues(cloneDeep(fullInitialValues));

      reset(fullInitialValues);
      trigger();
    }
  }, [agencies, homeAgency]);

  const onSubmitHandler = (values) => {
    dispatch(
      fetchReport(
        transformTextSummaryInput(values, ViewRetentionData),
        entityType.RETENTION,
        `PrintTextSummary`,
        () => {
          reset(values);
        }
      )
    );
  };

  const handlers = {
    onSubmit: () => handleSubmit(onSubmitHandler)(),
    onExport: () =>
      dispatch(
        exportReport(
          transformTextSummaryInput(getValues(), ViewRetentionData),
          reportName,
          entityType.RETENTION
        )
      ),
    onRestore: () => {
      reset(initialDefaultValues);
      dispatch(resetReportState());
    },
  };

  const buttons = [
    {
      action: "preview",
      disabled: !isValid,
    },
    {
      action: "export",
      disabled: !isValid,
      onClick: handlers.onExport,
    },
    { action: "restore", onClick: handlers.onRestore },
  ];

  return (
    <PermissionWrapper entityName="TextSummaryByReplyType">
      <ReportPanel
        isDirty={isDirty}
        isSubmitted={isSubmitted}
        onSubmit={handlers.onSubmit}
        buttons={buttons}
        taskId={ViewRetentionData}
      >
        <Row>
          <Col xs={24} sm={24} md={24} lg={10} xl={10} xxl={10}>
            <AgencyMultiSelectEx
              layout={specialLayout}
              formLabel="Agency"
              required={true}
              agencies={currentAgencies}
              isLoading={isEmpty(defaultValues.AgencyKeys)}
              setValue={setValue}
              control={control}
              name="AgencyKeys"
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={14} xl={14} xxl={14}>
            <AdvancedRangePicker
              formItem={{
                labelCol: {
                  xs: { span: 5 },
                  sm: { span: 7 },
                  md: { span: 6 },
                  lg: { span: 8 },
                  xl: { span: 6 },
                  xxl: { span: 5 },
                },
                wrapperCol: {
                  xs: { span: 19 },
                  sm: { span: 17 },
                  md: { span: 18 },
                  lg: { span: 16 },
                  xl: { span: 18 },
                  xxl: { span: 19 },
                },
                required: true,
                label: "Date",
              }}
              control={control}
              setValue={setValue}
              allowClear={true}
              name="ExpiryDate"
              format={dateFormat}
              dateRangeConfig={dateRangeConfigs.NON_TRANSACTION_NO_FUTURE}
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
            />
          </Col>
        </Row>
        <Row>
          <Col xs={24} sm={24} md={24} lg={10} xl={10} xxl={10}>
            <Form.Item
              name="Keyword"
              label="Keyword in Reply"
              {...specialLayout}
            >
              <Controller
                control={control}
                name="Keyword"
                render={({ field }) => <Input {...field} name="Keyword" />}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={24} lg={10} xl={12} xxl={12}>
            <Form.Item
              label="Include Late Renewals (up to 30 days)"
              name="IncludeLateRenewals"
              labelCol={{
                xs: { span: 10 },
                sm: { span: 14 },
                md: { span: 12 },
                lg: { span: 20 },
                xl: { span: 13 },
                xxl: { span: 10 },
              }}
              wrapperCol={{
                xs: { span: 14 },
                sm: { span: 10 },
                md: { span: 12 },
                lg: { span: 4 },
                xl: { span: 11 },
                xxl: { span: 14 },
              }}
              style={{
                marginBottom: 0,
              }}
            >
              <Controller
                control={control}
                name="IncludeLateRenewals"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => {
                      setValue("IncludeLateRenewals", e.target.checked, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    inputRef={field.ref}
                    name="IncludeLateRenewals"
                    disabled={
                      isEmpty(getValues().ExpiryDate[1]) ||
                      dayjs(getValues().ExpiryDate[1]) > dayjs()
                    }
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>
        <ExtraReportFilterContainer>
          <Row>
            <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
              <Form.Item
                name="ProducerCode1Keys"
                label="Producer 1"
                {...defaultLayout}
              >
                <ProducerSelectEx
                  name="ProducerCode1Keys"
                  setValue={setValue}
                  control={control}
                  producerNumber={1}
                  taskKey={ViewRetentionData}
                />
              </Form.Item>
              <Form.Item
                name="ProducerCode2Keys"
                label="Producer 2"
                {...defaultLayout}
              >
                <ProducerSelectEx
                  name="ProducerCode2Keys"
                  setValue={setValue}
                  control={control}
                  producerNumber={2}
                  taskKey={ViewRetentionData}
                />
              </Form.Item>
            </Col>
          </Row>
        </ExtraReportFilterContainer>

        <Row>
          <Col span={24}>
            <DrillDownSortFieldSelectEx
              fieldOptions={basicRetentionReportFieldOptions}
              control={control}
              trigger={trigger}
              isSubmitted={isSubmitted}
            />
          </Col>
        </Row>
      </ReportPanel>
    </PermissionWrapper>
  );
};
export default TextSummaryReport;
